import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Users, Search, X } from 'lucide-react';
import { Card, CardContent, Input } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { useSearchTutors } from '@/lib/student/search-tutors';
import { AvatarCircle } from '@/components/avatar-circle';

export function PlayerContactsPage() {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const { data: results = [], isLoading } = useSearchTutors(query);
  const playerId = user?.id ?? '';

  return (
    <div className="mx-auto max-w-lg space-y-6 px-4 py-6 pb-24 sm:py-8">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Users className="text-primary-600 h-6 w-6" />
        <h1 className="text-ink-900 text-2xl font-bold">Mon réseau</h1>
      </div>

      <p className="text-ink-500 -mt-3 font-sans text-sm">
        Découvre nos professeurs : consulte leur fiche publique et joue leurs exercices.
      </p>

      {/* Recherche */}
      <Card className="border-ink-200">
        <CardContent className="space-y-3 py-4">
          <p className="text-ink-900 text-sm font-semibold">Rechercher un professeur</p>
          <div className="relative">
            <Search className="text-ink-400 absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nom du professeur…"
              className="pl-9"
            />
            {query && (
              <button
                type="button"
                className="text-ink-400 absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setQuery('')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {query.trim().length >= 2 && (
            <div className="space-y-2">
              {isLoading ? (
                <p className="text-ink-400 text-sm">Recherche…</p>
              ) : results.length === 0 ? (
                <p className="text-ink-400 text-sm">Aucun résultat pour « {query} ».</p>
              ) : (
                results.map((tutor) => (
                  <Link
                    key={tutor.id}
                    to="/tutors/$tutorId"
                    params={{ tutorId: tutor.id }}
                    className="border-ink-100 hover:border-primary-200 flex items-center gap-3 rounded-xl border bg-white p-3 transition-colors active:scale-[0.99]"
                  >
                    <AvatarCircle name={tutor.display_name} src={tutor.avatar_url} size="sm" />
                    <p className="text-ink-900 truncate text-sm font-semibold">
                      {tutor.display_name}
                    </p>
                  </Link>
                ))
              )}
            </div>
          )}

          {query.trim().length > 0 && query.trim().length < 2 && (
            <p className="text-ink-400 text-xs">Saisis au moins 2 caractères pour rechercher.</p>
          )}
        </CardContent>
      </Card>

      {/* Ce qu'un Curieux peut (et ne peut pas encore) faire : pas de faux espoir. */}
      <div className="border-ink-100 ocean:bg-white/5 space-y-2 rounded-2xl border bg-white p-4">
        <p className="text-ink-800 text-sm font-semibold">Te faire suivre par un prof ?</p>
        <p className="text-ink-500 text-sm">
          Les liens avec un prof, des camarades ou un parent (devoirs, progression partagée) sont
          réservés aux comptes élève ou étudiant. Tu peux changer de type de compte depuis ton
          profil : tes exercices et tes SKILLCARDS sont conservés.
        </p>
        <Link
          to="/profile"
          className="text-primary-600 inline-flex min-h-[2.75rem] items-center text-sm font-semibold"
        >
          Ouvrir mon profil
        </Link>
      </div>

      {!playerId && (
        <p className="text-ink-400 text-center text-xs">
          Connecte-toi pour consulter les fiches des professeurs.
        </p>
      )}
    </div>
  );
}
